// -----------------------------------------------------------------------------
// BaseOWTrackingService — sends store analytics events to the Overwolf HTTP
// counter (analyticsnew.overwolf.com/analytics/Counter). Shared between the
// website (the embedded store iframe / plain web) and the ow-app in-app login
// page, each of which subclasses it with its own OwTrackingEnvironment.
//
// The two hosts differ in only two ways, so those are injected as an
// OwTrackingEnvironment and the send logic itself stays shared:
//   1. how the platform is decided ('owClient' vs 'Web') — the website checks
//      the iframe + injected window.overwolf; the native ow-app page is always
//      'owClient';
//   2. where the client dimensions (owver / MUID / MUIDV2) come from — the
//      website reads the injected window.overwolf, the ow-app page reads the
//      native overwolf.* APIs.
// -----------------------------------------------------------------------------

import { LoggerService } from '../logger.service';

const log = LoggerService.getCategory('OWTrackingService');

export type TrackingPlatform = 'owClient' | 'Web';

// Host-specific inputs the tracker needs; see the file header.
export interface OwTrackingEnvironment {
  // 'owClient' when running under the Overwolf client, otherwise 'Web'.
  getPlatform(): TrackingPlatform;
  // The client analytics dimensions (owver / MUID / MUIDV2), or null when the
  // client isn't ready yet so a later event retries. Only called on 'owClient'.
  loadOwParams(): Promise<Record<string, string> | null>;
}

// -----------------------------------------------------------------------------
export class BaseOWTrackingService {
  private userName: string | null = null;
  // The client dimensions, loaded once and cached (see ensureOwParams).
  private owParams: Record<string, string> | null = null;
  private owParamsPromise: Promise<Record<string, string>> | null = null;

  // ---------------------------------------------------------------------------
  constructor(private readonly env: OwTrackingEnvironment) {
    // Preload the client dimensions up front when we're under the client, so
    // they're ready by the time events fire and cost nothing per event.
    if (this.env.getPlatform() === 'owClient') {
      void this.ensureOwParams();
    }
  }

  // ---------------------------------------------------------------------------
  public setUserName(userName: string): void {
    this.userName = userName;
  }

  // ---------------------------------------------------------------------------
  // The client dimensions, loaded once and cached. The load is a round-trip, so
  // we do it a single time (concurrent callers share the in-flight promise). A
  // not-ready read isn't cached, so a later event retries.
  private ensureOwParams(): Promise<Record<string, string>> {
    if (this.owParams) {
      return Promise.resolve(this.owParams);
    }

    if (!this.owParamsPromise) {
      this.owParamsPromise = this.loadOwParams();
    }

    return this.owParamsPromise;
  }

  private async loadOwParams(): Promise<Record<string, string>> {
    const params = await this.env.loadOwParams();
    if (params) {
      this.owParams = params; // cache once we actually have the dimensions
      return params;
    }

    this.owParamsPromise = null; // client not ready — let a later event retry
    return {};
  }

  // ---------------------------------------------------------------------------
  public async track(name: string, extra?: any): Promise<void> {
    try {
      if (!extra) {
        extra = {};
      }

      const platform = this.env.getPlatform();
      extra.platform = platform;

      const params = new URLSearchParams();
      params.append('Name', name);
      params.append('Value', '0');

      if (this.userName) {
        params.append('UserName', this.userName);
      }
      params.append('Extra', encodeURIComponent(`${JSON.stringify(extra)}`));

      // Under the client, add the client version + machine ids as their own
      // top-level query params (owver / MUID / MUIDV2), not inside Extra.
      // Use the already-loaded dimensions synchronously when they're cached (they
      // are preloaded in the constructor). This keeps track() running straight
      // through to send() in the same tick, so an event fired immediately before
      // a synchronous teardown — e.g. login success right before the in-app login
      // iframe closes — still goes out. Only the first event, before the cache is
      // warm, awaits.
      if (platform === 'owClient') {
        const owParams = this.owParams ?? (await this.ensureOwParams());
        Object.keys(owParams).forEach((key) =>
          params.append(key, owParams[key]),
        );
      }

      const query = params.toString();
      const url = `https://analyticsnew.overwolf.com/analytics/Counter?${query}`;

      const xmlhttp = new XMLHttpRequest();
      xmlhttp.open('GET', url);
      xmlhttp.send();
    } catch (e) {
      log.error('sendAnalytic error', e);
    }
  }
}
